<template>
  <ui-dialog
    :show="isOpen"
    max-width="623"
    @input="handleClose"
    class="choose-reply-template"
  >
    <template #header>
      <div class="choose-reply-template__header">
        <h3>
          {{ $t("message.reviews.replyTemplates.chooseTemplate") }}
        </h3>
      </div>
    </template>

    <div class="category-select" v-if="categories">
      <small>{{
        $t("message.reviews.replyTemplates.templates.category")
      }}</small>
      <v-select
        v-model="selectedCategory"
        :items="categories"
        return-object
        item-text="name"
        item-value="id"
        :hide-details="true"
        :disabled="loading"
        dense
        append-icon="icon-arrow-down"
        full-width
        :label="$t('message.reviews.replyTemplates.templates.category')"
        :menu-props="{
          offsetY: true
        }"
        @input="selectCategory"
      >
      </v-select>
    </div>

    <div class="choose-reply-template__body">
      <ul
        class="choose-reply-template__list"
        v-if="templates && templates.length > 0"
      >
        <li
          v-for="template in templates"
          :key="template.id"
          class="template-list-item"
        >
          <p class="template-list-item__content">
            {{ template.text }}
          </p>
          <ui-button
            @click="useTemplate(template)"
            color="primary"
            min-width="130"
          >
            {{ $t("message.reviews.replyTemplates.use") }}
          </ui-button>
        </li>
      </ul>
      <div
        v-if="templates && templates.length === 0"
        class="choose-reply-template__empty"
      >
        <p>
          {{ $t("message.reviews.replyTemplates.templates.noTemplates") }}
        </p>
      </div>
      <SectionLoader :status="loading" :opacity="0.75" />
    </div>
  </ui-dialog>
</template>

<script>
import SectionLoader from "@/components/Loader/SectionLoader";

import serviceHandler from "@/helpers/handlers/service";
import reviewsService from "@/services/reviews";

export default {
  name: "ReplyTemplatesChooseTemplateDialog",
  components: {
    SectionLoader
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      categories: null,
      templates: null,
      selectedCategory: null
    };
  },

  computed: {
    categoriesIds() {
      return this.categories ? this.categories.map(({ id }) => id) : [];
    }
  },

  watch: {
    async isOpen(newValue) {
      if (newValue) {
        this.loading = true;
        await this.fetchReplyCategories();
        await this.fetchReplyTemplates();
        this.loading = false;
      } else {
        this.categories = null;
        this.templates = null;
        this.selectedCategory = null;
      }
    }
  },

  methods: {
    async fetchReplyCategories() {
      const response = await serviceHandler(
        reviewsService.getReplyCategories()
      );

      this.categories = [
        { id: null, name: this.$t("message.common.all") },
        ...response
      ];
    },

    async fetchReplyTemplates() {
      this.templates = await serviceHandler(
        reviewsService.getReplyTemplates({
          categoryIds: this.selectedCategory ? [this.selectedCategory.id] : []
        })
      );
    },

    async selectCategory(category) {
      this.loading = true;
      this.selectedCategory = category;
      await this.fetchReplyTemplates();
      this.loading = false;
    },

    useTemplate(template) {
      this.$emit("useTemplate", template);
      this.handleClose();
    },

    handleClose() {
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
.choose-reply-template {
  &__header {
    flex: 1 1 auto;
    text-align: center;
  }

  &__body {
    position: relative;
    min-height: 150px;
    padding: $spacing 0 $spacing * 2.5;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &__list {
    width: 100%;
    max-height: 500px;
    padding-left: 0;
    list-style: none;
    overflow-y: auto;
    color: $text;
  }

  &__empty {
    width: 100%;
    min-height: 140px;
    background-color: $light-4;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 25px;

    p {
      color: $text;
      text-align: center;
      max-width: 70%;
      white-space: pre-line;
      font-weight: 500;
      margin-bottom: 0;
    }
  }
}

.category-select {
  display: flex;
  flex-direction: column;
  padding-top: $spacing * 2.5;
  border-top: 1px solid $light-3;
  padding-bottom: $spacing * 2.5;
  margin-bottom: $spacing;
  border-bottom: 1px solid $light-3;

  small {
    line-height: 1;
  }
}

.template-list-item {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: $spacing * 2.5;
  padding: 0;
  margin-right: $spacing;

  &__content {
    width: 100%;
    padding: $spacing * 1.5 $spacing * 3.75;
    border-radius: 25px;
    background-color: $light-4;
    white-space: pre-line;
    margin-bottom: $spacing;
  }
}
</style>
